'use client';

import * as DS from '@aurorasolar/ds';
import { x } from '@xstyled/styled-components';
import { useEffect, useRef, useState } from 'react';

import { useTracking } from '@/analytics';
import { Section } from '@/app/lp/components';
import { TRACKING } from '@/helpers/tracking';

import {
  BillInputs,
  BillTotals,
  EstimateCta,
  InputMode,
  MonthlyBillChart,
  ProjectionMethodology,
  ProjectionPreview,
  RateChangeLedger,
  ScenarioSelector,
  SolarComparison,
} from './components';
import { knownRateChanges } from './data/knownRateChanges';
import { ProjectionScenario } from './data/rateProjections';
import { backcastDeepHistory, Ownership, projectMonthlyBills, runProjection, scenarioRange } from './engine';

type ToolState = {
  amount: string;
  homeSqft: number | null;
  inputMode: InputMode;
  ownership: Ownership | null;
  stateCode: string;
};

const DEFAULT_STATE: ToolState = { amount: '', homeSqft: null, inputMode: 'USD', ownership: null, stateCode: '' };

export const BillProjectionPage = () => {
  const { trackEvent } = useTracking();
  const [state, setState] = useState<ToolState>(DEFAULT_STATE);
  const [scenario, setScenario] = useState<ProjectionScenario>('BLEND');

  const amountNumber = Number(state.amount);
  // NaN > 0 is false, so no explicit finite check needed
  const hasAmount = amountNumber > 0;
  const usageInput = {
    monthlyBillUsd: state.inputMode === 'USD' ? amountNumber : undefined,
    monthlyKwh: state.inputMode === 'KWH' ? amountNumber : undefined,
    stateCode: state.stateCode,
  };
  // The chart's series comes first: the solar math walks this curve rather than
  // compounding an escalator of its own, so the payoffs can't drift from the plot
  const billPoints =
    hasAmount && state.stateCode ? projectMonthlyBills(usageInput, knownRateChanges, scenario) : undefined;
  const result = billPoints
    ? runProjection({ billPoints, homeSqft: state.homeSqft ?? undefined, scenario, stateCode: state.stateCode })
    : undefined;
  const range = billPoints ? scenarioRange(usageInput) : undefined;
  const deepHistory = billPoints ? backcastDeepHistory(usageInput) : undefined;

  const handleScenarioChange = (nextScenario: ProjectionScenario) => {
    setScenario(nextScenario);
    trackEvent(TRACKING.BILL_PROJECTION_SCENARIO_SWITCHED, { scenario: nextScenario, state: state.stateCode });
  };

  // Funnel events fire once per page view (brief Section 5 instrumentation)
  const hasStarted = useRef(false);
  const hasCompleted = useRef(false);

  // Primitive deps: `result` is a fresh object each render and would re-run the effect per keystroke
  const hasResult = result !== undefined;
  useEffect(() => {
    if (!hasStarted.current && hasAmount) {
      hasStarted.current = true;
      trackEvent(TRACKING.BILL_PROJECTION_STARTED, { inputMode: state.inputMode, state: state.stateCode });
    }
    if (!hasCompleted.current && hasResult) {
      hasCompleted.current = true;
      trackEvent(TRACKING.BILL_PROJECTION_COMPLETED, {
        monthlyBillUsd: state.inputMode === 'USD' ? amountNumber : null,
        monthlyKwh: state.inputMode === 'KWH' ? amountNumber : null,
        state: state.stateCode,
      });
    }
    // trackEvent is stable; each event fires once, on the first valid amount / first result
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAmount, hasResult]);

  return (
    <article>
      <DS.Flex
        as="header"
        gap={4}
        maxW="640px"
        mx="auto"
        pt={{ _: 8, md: 10 }}
        px={6}
        textAlign={{ _: 'left', md: 'center' }}
      >
        <x.h1 text={{ _: 'h2', md: 'd1' }}>
          <span>Your electric bill isn’t done climbing. </span>
          <span>See where it’s headed.</span>
        </x.h1>
        <DS.Text color="uiHelperCopy" text={{ _: 'body16', md: 'body20' }}>
          Enter your average monthly bill and your state to project how much your electricity costs will rise over the
          next 20 years, with and without solar.
        </DS.Text>
        <BillInputs
          amount={state.amount}
          inputMode={state.inputMode}
          onAmountChange={(amount) => setState((current) => ({ ...current, amount }))}
          // Clear the amount when the unit changes so a $ figure isn't silently reused as kWh (or vice versa)
          onInputModeChange={(inputMode) => setState((current) => ({ ...current, amount: '', inputMode }))}
          onStateChange={(stateCode) => setState((current) => ({ ...current, stateCode }))}
          stateCode={state.stateCode}
        />
      </DS.Flex>

      <Section align="center">
        <DS.Flex gap={5} maxW="900px" mx="auto" w="100%">
          {result && billPoints && range ? (
            <>
              {/* Tier 1, the tool: headline, chart, forecast control, and ledger
                  share one card. Full-bleed tinted band on mobile so the chart
                  gains width instead of losing it to padding.
                  The bleed needs `w: auto` + `alignSelf: stretch`: Section's centered
                  GridItem suppresses stretch, and DS.Flex's default `w: 100%` would
                  pin the width so the negative margins only shift the card left. */}
              <DS.Flex
                alignSelf={{ _: 'stretch', md: 'auto' }}
                bg="uiBgAlternate"
                borderRadius={{ _: 0, md: 16 }}
                gap={5}
                mx={{ _: -6, md: 0 }}
                px={{ _: 6, md: 7 }}
                py={{ _: 6, md: 7 }}
                w={{ _: 'auto', md: '100%' }}
              >
                <MonthlyBillChart
                  deepHistory={deepHistory}
                  points={billPoints}
                  scenarioRange={range}
                  scenarioSelector={<ScenarioSelector onScenarioChange={handleScenarioChange} scenario={scenario} />}
                />
                <RateChangeLedger deepHistory={deepHistory} points={billPoints} stateCode={state.stateCode} />
              </DS.Flex>
              <ProjectionMethodology />
              <BillTotals result={result} scenarioRange={range} />
              <SolarComparison
                ownership={state.ownership}
                result={result}
                scenario={scenario}
                stateCode={state.stateCode}
              />
              <EstimateCta ownership={state.ownership} stateCode={state.stateCode} />
            </>
          ) : (
            <ProjectionPreview />
          )}
        </DS.Flex>
      </Section>
    </article>
  );
};
